import { useState } from 'react'

import { useFloorPlanUploadProcessing } from './useFloorPlanUploadProcessing'
import { useToasts } from '@/hooks/useToasts'
import {
  MAX_FILENAME_LENGTH,
  MAX_PNG_SIZE,
  UPLOAD_ERROR_COMPONENTS,
  reportUploadError,
  useUpload,
} from '@/hooks/useUpload'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  useCreateFloorPlanMutation,
  useDeleteFloorPlanMutation,
  useUpdateFloorMutation,
} from '@/graphql/generated/hooks'
import { useFloorDetailsQuery } from '@/graphql/generated/hooks'
import { UpdateFloorInput, UploadedFileType } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { tVar } from '@/utils/i18n'

import { FloorFormInputsIProps } from '../types/types'
import {
  getErrorMessage,
  getSuccessMessage,
  isValidFileType,
  removeExtension,
} from '../utils/utils'

const FLOOR_PLAN_REFETCH_QUERIES = [
  'FloorDetails',
  'FacilityDevicesByFloors',
  'FacilityFloorPlan',
]

class FloorPlanReplacementCreationError extends Error {
  readonly cause: unknown

  constructor(cause: unknown) {
    super('Floor plan replacement creation failed after deletion')
    this.name = 'FloorPlanReplacementCreationError'
    this.cause = cause
  }
}

export const useEditFloor = ({ floorId, onClose, fileUploadWatch }) => {
  const me = useSelector(selectMe)
  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts()
  const [isUpdateLoading, setUpdateLoading] = useState(false)
  const [isFloorPlanMarkedForRemoval, setIsFloorPlanMarkedForRemoval] =
    useState(false)

  const [upload] = useUpload()
  const [updateFloor] = useUpdateFloorMutation()
  const [createFloorPlan] = useCreateFloorPlanMutation()
  const [deleteFloorPlan] = useDeleteFloorPlanMutation()

  const {
    selectedPdfPage,
    setSelectedPdfPage,
    numPdfPages,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    resetPdfPage,
    getFileForUpload,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
  } = useFloorPlanUploadProcessing({ fileUpload: fileUploadWatch })

  const { data: floorDetails, loading: isFloorDetailsLoading } =
    useFloorDetailsQuery({
      fetchPolicy: 'network-only',
      variables: { floorId: floorId },
    })

  const markFloorPlanForRemoval = () => setIsFloorPlanMarkedForRemoval(true)
  const undoFloorPlanRemoval = () => setIsFloorPlanMarkedForRemoval(false)
  const hasNewFile = Boolean(fileUploadWatch?.length)
  const isNewFileValid = !hasNewFile || isValidFileType(fileUploadWatch)
  const canUploadFloorPlan = Boolean(
    !floorDetails?.floor?.floorPlan || isFloorPlanMarkedForRemoval
  )
  const isFloorPlanSwapPending = Boolean(
    isFloorPlanMarkedForRemoval && hasNewFile && isNewFileValid
  )

  const resetFloorPlanChange = () => {
    resetPdfPage()
    resetCrop()
    undoFloorPlanRemoval()
  }

  const uploadFloorPlanFile = async (
    file: File
  ): Promise<{ name: string; s3Key: string }> => {
    const fileId = await upload({
      file,
      fileType: UploadedFileType.FloorPlan,
      validationOptions: {
        maxFileSizeInBytes: MAX_PNG_SIZE,
        maxFileNameLength: MAX_FILENAME_LENGTH,
      },
    })

    return {
      name: removeExtension(file.name),
      s3Key: `${me?.id}/${fileId}`,
    }
  }

  const update = async (input: UpdateFloorInput, file?: File) => {
    const currentFloorPlan = floorDetails?.floor?.floorPlan
    // Finish all client-side processing and upload the new asset before any
    // database mutation. A failed upload therefore leaves the current plan
    // and floor details untouched.
    const uploadedFloorPlan = file ? await uploadFloorPlanFile(file) : undefined

    if (input.name !== floorDetails?.floor?.name) {
      await updateFloor({
        variables: {
          input: {
            id: floorId,
            name: input.name,
          },
        },
        refetchQueries: [
          'facilities',
          'floorsByFacilityIds',
          'facilityFloorsDrawer',
          'floorPlanDetails',
          'FacilityFloorPlan',
        ],
      })
    }

    if (currentFloorPlan && isFloorPlanMarkedForRemoval) {
      if (uploadedFloorPlan) {
        // CreateFloorPlan rejects a floor that still has a plan, so the
        // uploaded replacement can only be created after removing the old one.
        await deleteFloorPlan({
          variables: { input: { id: currentFloorPlan.id } },
          refetchQueries: FLOOR_PLAN_REFETCH_QUERIES,
        })
        try {
          await createFloorPlan({
            variables: {
              input: {
                floorId: input.id,
                ...uploadedFloorPlan,
              },
            },
            refetchQueries: FLOOR_PLAN_REFETCH_QUERIES,
          })
        } catch (error) {
          throw new FloorPlanReplacementCreationError(error)
        }
      } else {
        await deleteFloorPlan({
          variables: { input: { id: currentFloorPlan.id } },
          refetchQueries: FLOOR_PLAN_REFETCH_QUERIES,
        })
      }
    } else if (!currentFloorPlan && uploadedFloorPlan) {
      await createFloorPlan({
        variables: {
          input: {
            floorId: input.id,
            ...uploadedFloorPlan,
          },
        },
        refetchQueries: FLOOR_PLAN_REFETCH_QUERIES,
      })
    }
  }

  const onUpdateFloor: SubmitHandler<FloorFormInputsIProps> = async (
    values
  ) => {
    setUpdateLoading(true)
    try {
      const file = values.file?.item(0)
      const input: UpdateFloorInput = {
        id: floorId,
        name: values.name,
        facilityId: values.facility?.value,
      }
      const pngFile = await getFileForUpload(file)
      await update(input, pngFile)
      showSuccess(
        isFloorPlanMarkedForRemoval && !pngFile
          ? tVar(t, 'floors:crud_remove_success', { name: input.name })
          : getSuccessMessage(input.name, 'update', pngFile)
      )
      setIsFloorPlanMarkedForRemoval(false)
      onClose()
    } catch (e) {
      reportUploadError(e, UPLOAD_ERROR_COMPONENTS.EditFloor)
      showError(
        e instanceof FloorPlanReplacementCreationError
          ? t('floors:crud_swap_create_error')
          : getErrorMessage(values.name, 'update', e)
      )
    } finally {
      setUpdateLoading(false)
    }
  }

  return {
    floorDetails,
    isFloorDetailsLoading,
    isUpdateLoading,
    isFloorPlanMarkedForRemoval,
    hasNewFile,
    isNewFileValid,
    canUploadFloorPlan,
    isFloorPlanSwapPending,
    setUpdateLoading,
    update,
    onUpdateFloor,
    markFloorPlanForRemoval,
    resetFloorPlanChange,
    showSuccess,
    showError,
    //Pdf props
    selectedPdfPage,
    numPdfPages,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    setSelectedPdfPage,
    resetPdfPage,
    pdfSelectorRef,
    // Crop props
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
  }
}
